import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import BASE_URL from '../api/config';
import { getCourses } from '../api/courses';

function Subscribe() {
  const navigate = useNavigate();
  const { t } = useTranslation('subscribe');
  const { isAr } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [firstCourseId, setFirstCourseId] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [email, setEmail] = useState(user?.email || '');

  useEffect(() => {
    fetch(`${BASE_URL}/lifetime-subscription/subscribers?limit=10`, {
      headers: { 'Accept': 'application/json' },
    })
      .then(r => r.json())
      .then(d => setSubscribers(d?.data || []))
      .catch(() => {});

    // get first course ID for checkout redirect
    getCourses({ per_page: 1 })
      .then(data => {
        const id = data?.data?.[0]?.id;
        if (id) setFirstCourseId(id);
      })
      .catch(() => {});
  }, []);

  const handleSubscribe = () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    if (firstCourseId) {
      navigate(`/checkout/${firstCourseId}`);
    } else {
      navigate('/courses');
    }
  };

  const handleGetAccess = async () => {
    const token = localStorage.getItem('token');
    const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
    if (!token || !currentUser) { navigate('/login'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE_URL}/lifetime-subscription/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email: email || currentUser.email }),
      });
      const data = await res.json();
      if (data?.data?.is_subscribed) { navigate('/course-videos'); }
      else { setError(data.message || 'Not subscribed yet.'); }
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen pt-20">

      {/* Hero Section */}
      <section
        className="w-full relative overflow-visible"
        style={{ background: 'linear-gradient(120deg, #f8f8f8 0%, #e0e0e0 40%, #cccccc 100%)', minHeight: '320px', paddingBottom: '80px' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between" style={{ minHeight: '320px' }}>
          {/* Left Text */}
          <div className="flex-1 py-8 md:py-12">
            <h1 className="font-semibold text-black mb-4" style={{ fontSize: 'clamp(2rem, 8vw, 96px)', lineHeight: 1 }}>{t('title')}</h1>
            <div className="relative inline-block rounded-full mb-4" style={{ minHeight: '40px', minWidth: '120px' }}>
              <img src="/nzrzOfxWPwo 1.png" alt="Background" className="absolute rounded-full"
                style={{ zIndex: 0, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <h2 className="relative px-4 py-2 font-bold text-white inline-block" style={{ zIndex: 10, fontSize: 'clamp(1rem, 4vw, 39px)' }}>
                {t('lifetime')}
              </h2>
            </div>
            <p className="text-gray-600 text-sm md:text-lg">{t('subtitle')}</p>
          </div>

          {/* Right - Cards image overflowing */}
          <div className="w-full md:flex-1 flex justify-center md:justify-end items-end" style={{ maxWidth: '480px', position: 'relative' }}>
            <img
              src="/b_عايز_كارد_زي_دول_بكو (1) 1.png"
              alt="Payment Cards"
              className="object-contain w-full md:w-auto"
              style={{ height: 'clamp(200px, 50vw, 420px)', position: 'relative', zIndex: 20, marginBottom: '-80px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      </section>

      {/* White section over hero */}
      <div style={{ position: 'relative', zIndex: 10, marginTop: '-60px' }}>
        <section className="bg-white px-2 lg:px-4 overflow-hidden" style={{ borderRadius: '60px 60px 0 0', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="max-w-7xl mx-auto">

            <div className="text-center mb-16 mt-16">
              <div className="flex flex-row gap-0 justify-center items-center mx-auto bg-gray-100 rounded-full border border-gray-300 overflow-hidden shadow-lg" style={{ maxWidth: '600px' }}>
                <input type="email" placeholder={t('emailPlaceholder')}
                  className="flex-1 px-5 py-3 bg-transparent border-none rounded-full text-black focus:outline-none text-sm min-w-0"
                  value={email} onChange={e => setEmail(e.target.value)} />
                <button onClick={handleGetAccess} disabled={loading}
                  className="px-6 py-3 bg-black text-white rounded-full font-semibold text-sm disabled:opacity-60">
                  {loading ? t('loading') : t('getAccess')}
                </button>
              </div>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              <div className="flex items-center justify-center gap-3 mt-4">
                <div className="flex -space-x-2">
                  {subscribers.length > 0 ? subscribers.slice(0, 3).map((sub, i) => (
                    sub.avatar_url
                      ? <img key={i} src={sub.avatar_url} alt={sub.name} className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                      : <div key={i} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold" style={{ background: `hsl(${i * 60}, 60%, 50%)` }}>{sub.name?.charAt(0)?.toUpperCase()}</div>
                  )) : (
                    <>
                      <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white"></div>
                      <div className="w-8 h-8 rounded-full bg-gray-400 border-2 border-white"></div>
                      <div className="w-8 h-8 rounded-full bg-gray-500 border-2 border-white"></div>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-700">{subscribers.length > 0 ? `${subscribers.length}+ ${t('studentsAccess')}` : `3 ${t('studentsAccess')}`}</p>
              </div>
            </div>

            {/* Black pricing box */}
            <div className="bg-black rounded-3xl px-8 py-10 text-white">

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-white text-3xl font-bold">SAR</span>
                  <span className="font-black" style={{ fontSize: '72px', lineHeight: 1, background: 'linear-gradient(90deg, #c8960c, #f5c518)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>1000</span>
                  <span className="text-gray-400 text-sm">{t('billedLifelong').split(' ')[0]}</span>
                </div>
                <p className="text-sm mt-1" style={{ color: '#A4FF00' }}>{t('internationalStandard')}</p>
              </div>

              {/* Subscribe Now button */}
              <button
                onClick={handleSubscribe}
                className="w-full py-4 rounded-full font-bold text-black text-lg mb-3"
                style={{ background: 'linear-gradient(90deg, #00e5ff, #a4ff00, #f5c518)' }}
              >
                {t('subscribeNow')}
              </button>

              {/* Try Free Lesson button */}
              <button
                onClick={() => navigate('/course-videos')}
                className="w-full py-4 rounded-xl font-semibold text-white text-lg mb-6 border border-gray-600 hover:border-gray-400 transition-colors"
              >
                {t('tryFree')}
              </button>

              {/* Plan row */}
              <div className="flex items-center justify-between bg-gray-900 rounded-xl px-5 py-4 mb-6">
                <div>
                  <p className="font-semibold text-white text-sm">{t('planName')}</p>
                  <p className="text-gray-400 text-xs">{t('billedLifelong')}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-10 h-5 bg-gray-600 rounded-full relative">
                      <div className="w-4 h-4 bg-white rounded-full absolute top-0.5 left-0.5"></div>
                    </div>
                    <span className="text-xs text-gray-400">{t('annualBilling')}</span>
                  </div>
                </div>
                <span className="font-bold text-white">SAR 1000</span>
              </div>

              {/* Divider + Before discount */}
              <div className="border-t border-gray-700 pt-4 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">{t('beforeDiscount')}</span>
                  <span className="font-bold text-lg" style={{ color: '#f5c518' }}>SAR <span className="text-2xl">1500</span></span>
                </div>
              </div>

              {/* Grand total */}
              <div className="border-t border-gray-700 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-white font-semibold">{t('grandTotal')}</span>
                  <span className="font-bold text-lg" style={{ color: '#f5c518' }}>SAR <span className="text-2xl">1000</span></span>
                </div>
              </div>

              {/* Payment methods */}
              <div className="flex items-center gap-3 flex-wrap mb-6">
                {['Mastercard', 'PayPal', 'VISA', 'iDEAL', 'Vodafone', 'Etisalat', 'Bitcoin', 'Binance'].map((method, i) => (
                  <div key={i} className="bg-white rounded-lg px-3 py-2 flex items-center justify-center" style={{ minWidth: '48px', height: '32px' }}>
                    <span className="text-xs font-bold text-black">{method.slice(0, 4)}</span>
                  </div>
                ))}
              </div>

              {/* Try Free Lesson again */}
              <button
                onClick={() => navigate('/course-videos')}
                className="border border-gray-600 rounded-xl px-6 py-3 text-white font-semibold text-sm mb-6 hover:border-gray-400 transition-colors"
              >
                {t('tryFree')}
              </button>

              {/* Reasons */}
              <div className="border border-gray-700 rounded-xl p-5">
                <p className="text-gray-400 text-sm mb-4">{t('reasonsTitle')}</p>
                <ul className="space-y-3">
                  {['r1', 'r2', 'r3', 'r4', 'r5'].map((key, i) => (
                    <li key={i} className="flex items-center gap-3 text-white text-sm">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#A4FF00' }}>
                        <svg className="w-3 h-3 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                        </svg>
                      </span>
                      {t(`reasons.${key}`)}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer - outside black box */}
            <div className="flex items-center justify-center gap-6 mt-6 px-2">
              <p className="text-gray-500 text-lg">{t('rights')}</p>
              <button className="text-gray-400 text-xs underline hover:text-black">{t('refundPolicy')}</button>
            </div>
          </div>
        </section>
      </div>

    </div>
  );
}

export default Subscribe;
